import { Button, Input, Modal, Select } from '@bennows/boows-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../lib/toasts';
import { operatorFailureKey } from './failureKey';
import { roleOptions } from './roleOptions';
import { useOperatorWrites, type Operator, type OperatorChanges, type OperatorRole } from './useOperators';

const FORM_ID = 'edit-operator';

/** Name and role of one operator; a new role signs the person out everywhere (the server does that). */
export function EditOperatorDialog({ operator, onClose }: { readonly operator: Operator; readonly onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { update } = useOperatorWrites();
  const [name, setName] = useState(operator.display_name);
  const [role, setRole] = useState<OperatorRole>(operator.role);
  const [missing, setMissing] = useState(false);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim() === '') return setMissing(true);
    const changes: OperatorChanges = {
      ...(name.trim() !== operator.display_name ? { display_name: name.trim() } : {}),
      ...(role !== operator.role ? { role } : {}),
    };
    if (Object.keys(changes).length === 0) return onClose();
    update.mutate(
      { id: operator.id, changes },
      {
        onSuccess: () => {
          toast(t('operators.editForm.saved'));
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('operators.editForm.title')}
      description={operator.email}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form={FORM_ID} loading={update.isPending}>
            {t('operators.editForm.submit')}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="ui-console-form" onSubmit={onSubmit} noValidate>
        <Input
          label={t('operators.editForm.name')}
          value={name}
          onChange={setName}
          maxLength={120}
          required
          invalid={missing}
          help={missing ? t('signIn.required') : undefined}
        />
        <Select
          label={t('operators.editForm.role')}
          options={roleOptions(t)}
          value={role}
          onChange={(id) => setRole(id as OperatorRole)}
          help={role !== operator.role ? t('operators.editForm.roleChangeNote') : undefined}
        />
        {update.error ? (
          <p className="ui-auth__message" role="alert">
            {t(operatorFailureKey(update.error, 'common.errorUnexpected'))}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
